import { autoinject, transient, computedFrom } from "aurelia-framework";
import { RouteConfig, Router } from "aurelia-router";
import { CargoTypeCategoryEnum, VesselDetailModel } from "../../../../models/vessels/vessel.models";
import { EntityTypeConfigurationModel, EntityTypeConfigurationHelper } from "../../../../models/basics/basics.models";
import { EntityFinderSearch } from "../../../components/entity-finder/entity-finder-search";
import { EntityValidationsService, ClientSideValidator } from "../../../../client-validation-system/validations-service";
import { ValidationActivation, ValidationTypeEnum } from "../../../../client-validation-system/validations.models";
import { FbrService } from "../../../../services/fbr/fbr.service";
import { NewFbrRequest, NewFbrQuestionRequest, FbrQuestionnaireTypesEnum } from "../../../../models/fbrs/fbr.models";
import { VetRuleOutcomeStepEnum } from "../../../../models/vetting/vetting.models";
import { RightShipQiRouterConstants } from "../../../../configuration/rightshipqi-router.constants";
import { UserClaimsService } from "../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../constants/authentication/claims";
import { ManageRightShipUsersClientService } from "../../../../services/modules/righship-qi-manage-users.service";
import { UserClientService } from "../../../../services/authentication/user-client.service";
import { ValidationsClientService } from "../../../../services/validations/validations-client.service";
import { ManageVesselService } from "../../../../services/vessels/manage-vessel.service";
import { NotificationType, NotificationClientService } from "../../../../services/basics/notification-client.service";
import { LookupClientService } from "../../../../services/lookups/lookup-client.service";
import { LookupValueRequest } from "../../../../models/lookups/lookup.models";
import * as Enumerable from "linq";
import { PortsAndTerminalsService } from "../ports-and-terminals/services/ports-and-terminals.service";

class FBRItemAnswerModel {
    outcome: VetRuleOutcomeStepEnum;
    fbrItemAnswerId: number;
    title: string;
    value: boolean;
    showInActivitiesInfo?: boolean;
    showInActivitiesTooltip?: boolean;
    tickReportThisToRightShipToReview: boolean;
}

class FBRItemQuestionModel {
    fbrItemId?: number;
    fbrItemQuestionId: number;
    title: string;
    description: string;
    answers: FBRItemAnswerModel[];
    comments: string;
    reportThisToRightShipToReview: boolean;
    attachments?: File[];
    addDescription?: boolean;
}
class FBRItemModel {
    id: number;
    title: string;
    icon: string;
    questions: FBRItemQuestionModel[];
}

@autoinject
@transient()
export class NewFbr {

    public vesselId: number;
    public dateOfOperation: Date = new Date();
    public portId: number;
    public terminal: string;
    public berth: string;
    public liftingCargoNumber: string;
    public comments: string;
    public loading: boolean = true;
    public discharge: boolean;
    public sts: boolean;
    public other: boolean;
    public fbrId: number;
    public onBehalfOf: number;
    public berthArrivalDate: Date;
    public berthDepartureDate: Date;
    public cargoTypeId: number;
    public tonnageHandled: string;
    public portEntityTypes: EntityTypeConfigurationModel[];
    public portVM: EntityFinderSearch;
    public currentStep: number = 1;
    public vessel: VesselDetailModel;
    public selectedForm: FBRItemModel;
    public _customerId: number;
    public isRightShipUser: boolean;
    public today: Date = new Date();
    vesselPortCallId: number;
    portBkey: number;
    portName: string;
    buBehalfRule: { errorMessage: string; parameters: any; property: string; validationType: ValidationTypeEnum; };
    isPTFbr: boolean = false;

    @computedFrom("_customerId")
    public get customerId() {
        return this._customerId;
    }

    public set customerId(value: number) {
        this._customerId = value;

        if (!this._customerId) {
            this.onBehalfOf = null;
        }
    }

    public validator: ClientSideValidator;

    constructor(
        private lookupClientService: LookupClientService,
        private router: Router,
        private entityValidationsService: EntityValidationsService,
        private entityTypeConfigurationHelper: EntityTypeConfigurationHelper,
        private manageVesselService: ManageVesselService,
        private fbrService: FbrService,
        private notificationClientService: NotificationClientService,
        private validationsClientService: ValidationsClientService,
        private portsAndTerminalsService: PortsAndTerminalsService,
        userClaimsService: UserClaimsService,
        userClientService: UserClientService,
        manageRightShipUsersClientService: ManageRightShipUsersClientService) {
        this.portEntityTypes = this.entityTypeConfigurationHelper.getConfigurationWithoutPermissions(["Ports"]);
        this.isRightShipUser = userClaimsService.hasPermission(Claims.RightShipUser);

        manageRightShipUsersClientService.getBusinessUnitUser(userClientService.currentUser.id)
            .then(user => {
                this.onBehalfOf = user.businessUnit ? user.businessUnit.businessUnitId : this.onBehalfOf;
            });
    }

    private async save() {

        const request: NewFbrRequest = {

            portId: this.portId,
            terminal: this.terminal,
            berth: this.berth,
            comments: this.comments,
            loading: this.loading,
            discharge: this.discharge,
            sts: this.sts,
            other: this.other,
            fbrQuestioner: this.selectedForm.id,
            dateReported: this.dateOfOperation,
            questions: this.selectedForm.questions.map<NewFbrQuestionRequest>(x => {
                return <NewFbrQuestionRequest>{
                    fbrItemId: x.fbrItemId,
                    fbrItemQuestionId: x.fbrItemQuestionId,
                    description: x.description,
                    fbrItemAnswerId: x.answers.filter(a => a.value).map(a => a.fbrItemAnswerId)[0],
                    comments: x.comments,
                    showInActivitiesInfo: x.answers.filter(a => a.value).map(a => a.showInActivitiesInfo)[0],
                    showInActivitiesTooltip: x.answers.filter(a => a.value).map(a => a.showInActivitiesTooltip)[0],
                    rightShipToReview: x.reportThisToRightShipToReview,
                    attachments: x.attachments
                };
            }),

            meta: JSON.stringify({
                liftingCargoNumber: this.liftingCargoNumber,
                onBehalfOf: this.onBehalfOf,
                berthDepartureDate: this.berthDepartureDate,
                berthArrivalDate: this.berthArrivalDate,
                dateOfOperation: this.dateOfOperation,
                cargoTypeId: this.cargoTypeId,
                tonnageHandled: this.tonnageHandled
            }),
            onBehalfOfBusinessUnitId: this.onBehalfOf,
            vesselId: this.vessel.vesselId,
            fbrId: this.fbrId,
            vesselPortCallId: this.vesselPortCallId
        };

        return new Promise<void>((resolve, reject) => {
            const savePromise = this.isPTFbr ? this.portsAndTerminalsService.submitFbr(request) :
                this.fbrService.save(request);
            savePromise.then(response => {
                if (response.validationResult && response.validationResult.hasError) {
                    this.validationsClientService.showValidationErrors(response.validationResult);
                    reject();
                } else {
                    for (const q of response.questions) {
                        const question = this.selectedForm.questions.filter(x => x.fbrItemQuestionId == q.fbrItemQuestionId)[0];
                        if (question)
                            question.fbrItemId = q.fbrItemId;
                    }
                    this.fbrId = response.fbrId;
                    resolve();
                }
            }).catch(response => {
                if (response && response.validationResult && response.validationResult.hasError) {
                    this.validationsClientService.showValidationErrors(response.validationResult);
                } else {
                    this.notificationClientService.show("Error submitting Feedback Report", "", NotificationType.Error);
                }
                reject();
            });

        });
    }

    answerChanged(item: FBRItemQuestionModel, answer: FBRItemAnswerModel) {
        item.reportThisToRightShipToReview = answer.tickReportThisToRightShipToReview;
        item.addDescription = answer.tickReportThisToRightShipToReview;
    }

    addDescriptionChanged(event, item: FBRItemQuestionModel) {
        if (!item.addDescription) {
            item.description = "";
            item.attachments = [];
        }
    }

    async activate(params: { vesselId: number, cargoTypeCategoryId: number, vesselBkey: string, data: FbrInitalDataModel }, navigationInstruction: RouteConfig) {

        this.isPTFbr = navigationInstruction?.route == RightShipQiRouterConstants.RouteNames.PTSubmitFbr;

        this.vesselId = Number(params.vesselId);
        try {
            const lq = new LookupValueRequest();
            lq.lookupValueQuery = "RS_FbrItemQuestions";
            const la = new LookupValueRequest();
            la.lookupValueQuery = "RS_FbrItemAnswers";
            const questions = await this.lookupClientService.getLookup(lq);
            const answers = await this.lookupClientService.getLookup(la);
            const gq = Enumerable.from(questions.activeValues).groupBy(x => x.additionalData.fbrReport);

            let fbrReport = FbrQuestionnaireTypesEnum.DryBerthReport;

            if (params && params.cargoTypeCategoryId == CargoTypeCategoryEnum.Wet) {
                fbrReport = FbrQuestionnaireTypesEnum.TankerTerminalReport;
            }

            const reportQuestions = gq.first(x => x.key() == fbrReport).orderBy(x => x.sequenceNumber).select(x => {
                return {
                    fbrItemQuestionId: x.id,
                    title: x.name,
                    description: "",
                    answers: Enumerable.from(answers.activeValues)
                        .orderBy(a => a.sequenceNumber)
                        .where(a => a.additionalData.fbrItemQuestionId == x.id)
                        .select(a => {
                            return {
                                fbrItemAnswerId: a.id,
                                title: a.name,
                                value: a.additionalData.value,
                                tickReportThisToRightShipToReview: a.additionalData.reportThisToRightShipToReview,
                                outcome: a.additionalData.outcome
                            }
                        }).toArray(),
                    comments: "",
                    reportThisToRightShipToReview: false
                };
            }).toArray();
            const response = await this.manageVesselService.getVessel({
                vesselId: this.vesselId,
                imo: null,
                bKey: params.vesselBkey
            });
            //when bkey is provided
            if (!this.vesselId) {
                this.vesselId = response.vessel.vesselId;
            }
            if (!params?.cargoTypeCategoryId && response.vessel.cargoTypeCategoryId == CargoTypeCategoryEnum.Wet) {
                fbrReport = FbrQuestionnaireTypesEnum.TankerTerminalReport;
            }
            this.vessel = response.vessel;
            this.selectForm({
                id: fbrReport,
                icon: fbrReport == FbrQuestionnaireTypesEnum.DryBerthReport ?
                    `M23.9,15.4c-0.1-0.3-0.4-0.4-0.7-0.4h-7.5c-0.4,0-0.8,0.3-0.8,0.8v0.8H9v-0.8C9,15.3,8.7,15,8.2,15H7.5v-1.5
                    h0.8c0.4,0,0.7-0.3,0.8-0.8v-2.2c0-0.2-0.1-0.4-0.2-0.5L8.6,9.8L18,2.3V9h-0.8c-0.4,0-0.8,0.3-0.8,0.8v3c0,0.4,0.3,0.7,0.8,0.8h3
                    c0.4,0,0.7-0.3,0.8-0.8v-3C21,9.3,20.7,9,20.2,9h-0.8V0.8c0-0.3-0.2-0.6-0.4-0.7c-0.3-0.1-0.6-0.1-0.8,0.1L7.6,8.7l-1-1
                    C6.4,7.6,6.2,7.5,6,7.5H2.2c-0.4,0-0.8,0.3-0.8,0.8v4.5c0,0.4,0.3,0.7,0.8,0.8H3V15H0.8c-0.2,0-0.4,0.1-0.6,0.3
                    C0,15.5,0,15.7,0,15.9l1.5,6c0.1,0.3,0.4,0.6,0.7,0.6h16.5c0.2,0,0.5-0.1,0.6-0.3l4.5-6C24,16,24,15.7,23.9,15.4z M19.5,12H18v-1.5
                    h1.5V12z M3,9h2.7l1.8,1.8V12H3V9z M4.5,13.5H6V15H4.5V13.5z M18.4,21H2.8l-1.1-4.5h5.8v0.8c0,0.4,0.3,0.7,0.8,0.8h7.5
                    c0.4,0,0.7-0.3,0.8-0.8v-0.8h5.2L18.4,21z` :
                    `M10.9,3.2C10.9,1.4,9.5,0,7.8,0S4.6,1.3,4.6,3.1c0,1.4,0.9,2.7,2.3,3.1v3.3h-5C1.3,9.5,1,10,1,10.5
                    c0,0.4,0.4,0.8,0.8,0.8h5v8.2c-2.3-0.3-4.3-1.9-5.1-4.1c-0.1-0.5-0.6-0.8-1.1-0.7c-0.5,0.1-0.8,0.6-0.7,1.1c0,0.1,0,0.1,0.1,0.2
                    c1,2.9,3.7,5,6.8,5.4v1.7c0,0.5,0.4,0.9,0.9,0.9c0.5,0,0.9-0.4,0.9-0.9c0,0,0,0,0,0V6.2C10,5.8,10.9,4.6,10.9,3.2z M7.8,4.5
                    C7,4.5,6.4,3.9,6.4,3.2c0-0.8,0.6-1.4,1.4-1.4s1.4,0.6,1.4,1.4v0C9.1,3.9,8.5,4.5,7.8,4.5z M10.9,9.5h3.2c0.5-0.1,0.9,0.3,1,0.8
                    c0.1,0.5-0.3,0.9-0.8,1c-0.1,0-0.1,0-0.2,0h-3.2c-0.5,0.1-0.9-0.3-1-0.8c-0.1-0.5,0.3-0.9,0.8-1C10.8,9.5,10.9,9.5,10.9,9.5z
                    M15.4,16c-0.8,2.1-2.4,3.9-4.4,4.8c-0.5,0.2-1,0-1.2-0.5c0,0,0,0,0,0c-0.2-0.5,0-1,0.5-1.2c1.6-0.7,2.9-2,3.5-3.7
                    c0.2-0.5,0.7-0.7,1.1-0.6c0,0,0,0,0,0c0.2,0.1,0.4,0.2,0.5,0.5C15.5,15.5,15.5,15.8,15.4,16z`,
                title: fbrReport == FbrQuestionnaireTypesEnum.DryBerthReport ? "Dry Berth Report" : "Tanker Terminal Report",
                questions: reportQuestions
            });

            //set bu for rs users
            if (this.isRightShipUser && params.data && params.data.businessUnitId > 0) {
                this.customerId = params.data.customerId;
                this.onBehalfOf = params.data.businessUnitId;
            }

            if (params.data) {
                this.vesselPortCallId = params.data.vesselPortCallId;
                //set port, terminal and berth for port call
                this.portBkey = params.data.portBKey;
                this.portName = params.data.portName;
                this.terminal = params.data.terminalName;
                this.berth = params.data.berthName;
                //set bert arrival date adn departure date for port call
                this.berthArrivalDate = params.data.eta;
                this.berthDepartureDate = params.data.etd;
            }
        } catch (e) {
            console.error("Error loading FBR", e);
        }
    }

    attached() {
        setTimeout(() => {
            if (this.portBkey && this.portVM) {
                this.portVM.filterText = this.portBkey.toString();
                this.portVM.setFirst(this.portBkey.toString(), this.portEntityTypes).then(() => {
                    //fallback try to find by name
                    if (!this.portVM.selectedEntityId && this.portName && this.portName.trim() != "") {
                        this.portVM.filterText = this.portName;
                        this.portVM.setFirst(this.portName, this.portEntityTypes);
                    }
                });
            }
        });
    }

    selectForm(form: FBRItemModel) {
        this.selectedForm = form;

        // portId: this.portId,
        // terminal: this.terminal,
        // berth: this.berth,
        // comments: this.comments

        const rulesHolder = {
            activation: ValidationActivation.Always,
            entityName: "FBR Request",
            isCollection: false,
            nestedRulesHolder: [],
            rules: [{
                errorMessage: "Port is required",
                parameters: null, property: "portId",
                validationType: ValidationTypeEnum.Required
            }, {
                errorMessage: "Terminal is required",
                parameters: null, property: "terminal",
                validationType: ValidationTypeEnum.Required
            }, {
                errorMessage: "Comments are required",
                parameters: null, property: "comments",
                validationType: ValidationTypeEnum.Required
            }]
        };

        if (this.selectedForm.id == 1) {
            rulesHolder.rules.push({
                errorMessage: "Date of operation is required",
                parameters: null, property: "dateOfOperation",
                validationType: ValidationTypeEnum.Required
            });
        } else if (this.selectedForm.id == 2) {
            rulesHolder.rules.push({
                errorMessage: "Report date is required",
                parameters: null, property: "dateOfOperation",
                validationType: ValidationTypeEnum.Required
            });
            rulesHolder.rules.push({
                errorMessage: "Berth Departure date is required",
                parameters: null, property: "berthDepartureDate",
                validationType: ValidationTypeEnum.Required
            });
            rulesHolder.rules.push({
                errorMessage: "Berth Arrival date is required",
                parameters: null, property: "berthArrivalDate",
                validationType: ValidationTypeEnum.Required
            });
            rulesHolder.rules.push({
                errorMessage: "Cargo Type is required",
                parameters: null, property: "cargoTypeId",
                validationType: ValidationTypeEnum.Required
            });
            rulesHolder.rules.push({
                errorMessage: "Tonnage Handled is required",
                parameters: null, property: "tonnageHandled",
                validationType: ValidationTypeEnum.Required
            });
        }

        this.validator = this.entityValidationsService.getValidator(this, rulesHolder);
    }

    async next() {
        if (this.currentStep == 1) {
            const valid = await this.validator.validate();
            if (valid.valid) {
                await this.save();
                this.currentStep++;
            }
        }
        else if (this.currentStep == 2) {
            this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.Fbr, { id: this.fbrId });
        }
    }

    goBack() {
        // clear data
        this.fbrId = null;
        if (this.currentStep == 1) {
            this.router.navigateBack();
        } else if (this.currentStep == 2) {
            if (this.isPTFbr) {
                this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.PortRiskProfile, { id: this.vessel.vesselId });
            }
            else {
                this.router.navigateToRoute(RightShipQiRouterConstants.RouteNames.VesselDetails, { id: this.vessel.vesselId });
            }
        }
    }

    public onPortSelected(port) {
        this.portId = port ? port.id : null;
    }

}

export interface FbrInitalDataModel {
    customerId?: number;
    businessUnitId?: number;
    portBKey?: number;
    portName?: string;
    terminalName?: string;
    berthName?: string;
    vesselPortCallId?: number;
    eta?: Date;
    etd?: Date;
}
